import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { AxiosRequestConfig } from 'axios';
import { useRef } from 'react';
import { msalScopes } from '../constants/msalConfig';
import { axiosRequest } from '../utils';
import { ErrorType, logError } from '../utils/splunkLogger';

export const useAuthAxiosRequest = (keyToken?: string) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const redirectInProgress = useRef(false);

  const getAccessToken = async (): Promise<string | null> => {
    if (redirectInProgress.current) return null;
    if (!account) return null;

    try {
      const { accessToken } = await instance.acquireTokenSilent({
        ...msalScopes,
        account: account,
      });

      return accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        redirectInProgress.current = true;

        await instance.handleRedirectPromise();
        instance.acquireTokenRedirect({
          ...msalScopes,
          account: account,
        });
        return null;
      }

      logError({
        error: err as Error,
        errorType: ErrorType.AUTH_ERROR,
        info: { message: 'Failed to get access token' },
      });
    }

    return null;
  };

  const authAxiosRequest = async <T>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): ReturnType<typeof axiosRequest<T>> => {
    const accessToken = await getAccessToken();
    let authorizationHeaderVal = '';

    if (accessToken) {
      authorizationHeaderVal = `Bearer ${accessToken}`;
    } else if (keyToken) {
      authorizationHeaderVal = `Basic ${keyToken}`;
    }

    const updatedConfig: AxiosRequestConfig = {
      ...config,
      headers: { ...config?.headers },
    };

    if (authorizationHeaderVal) {
      updatedConfig.headers = {
        ...updatedConfig.headers,
        Authorization: authorizationHeaderVal,
      };
    }

    return axiosRequest<T>(endpoint, updatedConfig);
  };

  return { authAxiosRequest };
};
